import { useState } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSelect from "components/SoftSelect";

const NONPROFIT_CATEGORIES = [
  { value: "medical", label: "Medical" },
  { value: "animal-rescue", label: "Animal Rescue" },
  { value: "disaster-relief", label: "Disaster Relief" },
  { value: "veteran-support", label: "Veteran Support" },
  { value: "conservation", label: "Conservation" },
  { value: "aviation-experience", label: "Aviation Experience" },
  { value: "other", label: "Other" },
];

const LOGO_ACCEPT =
  "image/png,image/jpeg,image/webp,image/gif,image/svg+xml,.png,.jpg,.jpeg,.webp,.gif,.svg";

function isImageLogoFile(file) {
  if (!file) return false;
  if (file.type && file.type.startsWith("image/")) return true;
  return /\.(png|jpe?g|webp|gif|svg)$/i.test(file.name || "");
}

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

function NonprofitForm({
  onSubmit,
  onCancel,
  availableUsers = [],
  initialData = null,
  loading = false,
}) {
  const addressFromInitial = initialData?.address ?? { address: "", city: "", state: "" };
  const opsFromInitial = initialData?.operationalStates || [];
  const addressStateFromInitial = addressFromInitial?.state || "";
  const operationalStatesInitial =
    addressStateFromInitial && !opsFromInitial.includes(addressStateFromInitial)
      ? [addressStateFromInitial, ...opsFromInitial]
      : opsFromInitial;

  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    ein: initialData?.ein || "",
    websiteUrl: initialData?.websiteUrl || "",
    email: initialData?.email || "",
    phoneNumber: initialData?.phoneNumber || "",
    address: addressFromInitial,
    category: initialData?.category || "",
    customCategory: initialData?.customCategory || "",
    description: initialData?.description || "",
    logo: initialData?.logo || null,
    status: initialData?.status || "active",
    operationalStates: operationalStatesInitial,
  });

  const [errors, setErrors] = useState({});
  const [logoPreview, setLogoPreview] = useState(
    typeof initialData?.logo === "string" ? initialData.logo : null
  );

  // Phone number validation function - US format: (123) 123-1234
  const validatePhoneNumber = (phone) => {
    if (!phone || !phone.trim()) return null;
    // Remove all non-digit characters and check length
    const digitsOnly = phone.replace(/\D/g, "");
    if (digitsOnly.length !== 10) {
      return "Phone number must be exactly 10 digits in format (123) 123-1234";
    }
    // Check if the format matches (XXX) XXX-XXXX
    const usPhoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    if (!usPhoneRegex.test(phone.trim())) {
      return "Phone number must be in format (123) 123-1234";
    }
    return null;
  };

  // Phone number formatting function - US format: (123) 123-1234
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, "");
    // Limit to exactly 10 digits for US format
    const limitedDigits = digitsOnly.slice(0, 10);

    // Format as (XXX) XXX-XXXX for US numbers (exactly 10 digits)
    if (limitedDigits.length <= 3) {
      return limitedDigits;
    } else if (limitedDigits.length <= 6) {
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
    } else {
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(
        3,
        6
      )}-${limitedDigits.slice(6)}`;
    }
  };

  // EIN validation function
  const validateEIN = (ein) => {
    if (!ein || !ein.trim()) return "EIN is required";
    // Remove all non-digit characters to check digits only
    const digitsOnly = ein.replace(/\D/g, "");
    if (digitsOnly.length !== 9) {
      return "EIN must contain exactly 9 digits";
    }
    // Check format: XX-XXXXXXX (2 digits, hyphen, 7 digits)
    if (!/^\d{2}-\d{7}$/.test(ein)) {
      return "EIN must be in format XX-XXXXXXX";
    }
    return null;
  };

  const handleChange = (field, value) => {
    // Format EIN input as user types: XX-XXXXXXX
    if (field === "ein") {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, "");
      // Limit to 9 digits
      const limitedDigits = digitsOnly.slice(0, 9);
      // Format: XX-XXXXXXX
      let formatted = limitedDigits;
      if (limitedDigits.length > 2) {
        formatted = `${limitedDigits.slice(0, 2)}-${limitedDigits.slice(2)}`;
      }
      value = formatted;
    }

    // Format phone numbers as user types
    if (field === "phoneNumber") {
      value = formatPhoneNumber(value);
    }

    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!isImageLogoFile(file)) {
      setErrors({ ...errors, logo: "Please select a single image file" });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors({ ...errors, logo: "File size should be less than 5MB" });
      return;
    }

    setFormData({ ...formData, logo: file });

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);

    if (errors.logo) {
      setErrors({ ...errors, logo: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Organization name is required";
    }

    const einError = validateEIN(formData.ein);
    if (einError) {
      newErrors.ein = einError;
    }

    if (!formData.email.trim()) {
      newErrors.email = "Organization email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.category) {
      newErrors.category = "Non-profit category is required";
    }

    // If "other" is selected, custom category is required
    if (formData.category === "other" && !formData.customCategory.trim()) {
      newErrors.customCategory = "Please specify the category";
    }

    // Validate single address
    const addr = formData.address || {};
    if (!addr.address || !addr.address.trim()) {
      newErrors.address_street = "Street address is required";
    }
    if (!addr.city || !addr.city.trim()) {
      newErrors.address_city = "City is required";
    }
    if (!addr.state || !addr.state.trim()) {
      newErrors.address_state = "State is required";
    }

    if (formData.phoneNumber.trim()) {
      const phoneError = validatePhoneNumber(formData.phoneNumber);
      if (phoneError) {
        newErrors.phoneNumber = phoneError;
      }
    }

    if (!formData.operationalStates || formData.operationalStates.length === 0) {
      newErrors.operationalStates = "At least one operational state is required";
    }

    // EXTRA FIELD VALIDATION - Commented out (not in schema)
    // if (!formData.firstAdminId) {
    //   newErrors.firstAdminId = "First admin is required";
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <SoftBox>
      <Grid container spacing={1}>
        {/* Organization Name */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Organization Name *
            </SoftTypography>
          </SoftBox>
          <SoftInput
            placeholder="Enter organization name"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            error={!!errors.name}
          />
          {errors.name && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.name}
            </SoftTypography>
          )}
        </Grid>

        {/* EIN */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              EIN (Employer Identification Number) *
            </SoftTypography>
          </SoftBox>
          <SoftInput
            placeholder="XX-XXXXXXX"
            value={formData.ein}
            onChange={(e) => handleChange("ein", e.target.value)}
            error={!!errors.ein}
          />
          {errors.ein && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.ein}
            </SoftTypography>
          )}
        </Grid>

        {/* Status */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Status
            </SoftTypography>
          </SoftBox>
          <SoftSelect
            placeholder="Select Status"
            options={STATUS_OPTIONS}
            value={STATUS_OPTIONS.find(
              (opt) => opt.value === formData.status
            )}
            onChange={(option) => handleChange("status", option?.value || "active")}
          />
        </Grid>

        {/* Organization Email */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Organization Email *
            </SoftTypography>
          </SoftBox>
          <SoftInput
            type="email"
            placeholder="contact@organization.org"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            error={!!errors.email}
          />
          {errors.email && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.email}
            </SoftTypography>
          )}
        </Grid>

        {/* Website URL */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Website URL
            </SoftTypography>
          </SoftBox>
          <SoftInput
            placeholder="https://www.organization.org"
            value={formData.websiteUrl}
            onChange={(e) => handleChange("websiteUrl", e.target.value)}
          />
        </Grid>

        {/* Phone Number */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Organization Phone Number
            </SoftTypography>
          </SoftBox>
          <SoftInput
            type="tel"
            placeholder="(XXX) XXX-XXXX"
            value={formData.phoneNumber}
            onChange={(e) => handleChange("phoneNumber", e.target.value)}
            error={!!errors.phoneNumber}
          />
          {errors.phoneNumber && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.phoneNumber}
            </SoftTypography>
          )}
        </Grid>

        {/* Non-Profit Category */}
        <Grid item xs={12} md={4}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Non-Profit Category *
            </SoftTypography>
          </SoftBox>
          <SoftSelect
            placeholder="Select Category"
            options={NONPROFIT_CATEGORIES}
            value={NONPROFIT_CATEGORIES.find(
              (opt) => opt.value === formData.category
            )}
            onChange={(option) => {
              const newCategory = option?.value || "";
              handleChange("category", newCategory);
              // Clear custom category when switching away from "other"
              if (newCategory !== "other") {
                setFormData({ ...formData, category: newCategory, customCategory: "" });
                if (errors.customCategory) {
                  setErrors({ ...errors, customCategory: "" });
                }
              }
            }}
            error={!!errors.category}
          />
          {errors.category && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.category}
            </SoftTypography>
          )}
        </Grid>

        {/* Custom Category - Show only when "other" is selected */}
        {formData.category === "other" && (
          <Grid item xs={12} md={4}>
            <SoftBox mb={0.5}>
              <SoftTypography variant="caption" fontWeight="bold">
                Specify Category *
              </SoftTypography>
            </SoftBox>
            <SoftInput
              placeholder="Enter custom category"
              value={formData.customCategory}
              onChange={(e) => handleChange("customCategory", e.target.value)}
              error={!!errors.customCategory}
            />
            {errors.customCategory && (
              <SoftTypography
                variant="caption"
                color="error"
                mt={0.25}
                display="block"
              >
                {errors.customCategory}
              </SoftTypography>
            )}
          </Grid>
        )}

        {/* Address (single) */}
        <Grid item xs={12}>
          <SoftTypography variant="button" fontWeight="bold" display="block" mt={1.5} mb={1}>
            Address *
          </SoftTypography>
          {/* <SoftBox
            p={2}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
          > */}
            <Grid container spacing={1}>
              <Grid item xs={12} md={4}>
                <SoftBox mb={0.5}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Street Address *
                  </SoftTypography>
                </SoftBox>
                <SoftInput
                  placeholder="Enter street address"
                  value={formData.address?.address ?? ""}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      address: { ...formData.address, address: e.target.value },
                    });
                    if (errors.address_street) setErrors({ ...errors, address_street: "" });
                  }}
                  error={!!errors.address_street}
                />
                {errors.address_street && (
                  <SoftTypography variant="caption" color="error" mt={0.25} display="block">
                    {errors.address_street}
                  </SoftTypography>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <SoftBox mb={0.5}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    City *
                  </SoftTypography>
                </SoftBox>
                <SoftInput
                  placeholder="Enter city"
                  value={formData.address?.city ?? ""}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      address: { ...formData.address, city: e.target.value },
                    });
                    if (errors.address_city) setErrors({ ...errors, address_city: "" });
                  }}
                  error={!!errors.address_city}
                />
                {errors.address_city && (
                  <SoftTypography variant="caption" color="error" mt={0.25} display="block">
                    {errors.address_city}
                  </SoftTypography>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <SoftBox mb={0.5}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    State *
                  </SoftTypography>
                </SoftBox>
                <SoftSelect
                  placeholder="Select State"
                  options={US_STATES.map((state) => ({ value: state, label: state }))}
                  value={
                    US_STATES.map((state) => ({ value: state, label: state })).find(
                      (opt) => opt.value === formData.address?.state
                    ) || null
                  }
                  onChange={(option) => {
                    const newState = option?.value ?? "";
                    const oldState = formData.address?.state || "";
                    const ops = formData.operationalStates || [];
                    const withoutOld = oldState ? ops.filter((s) => s !== oldState) : ops;
                    const nextOps =
                      newState && !withoutOld.includes(newState)
                        ? [...withoutOld, newState]
                        : withoutOld;
                    setFormData({
                      ...formData,
                      address: { ...formData.address, state: newState },
                      operationalStates: nextOps,
                    });
                    if (errors.address_state) setErrors({ ...errors, address_state: "" });
                  }}
                  error={!!errors.address_state}
                />
                {errors.address_state && (
                  <SoftTypography variant="caption" color="error" mt={0.25} display="block">
                    {errors.address_state}
                  </SoftTypography>
                )}
              </Grid>
            </Grid>
          {/* </SoftBox> */}
        </Grid>

        {/* Operational States (multi-select) */}
        <Grid item xs={12} md={6}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Operational States *
            </SoftTypography>
          </SoftBox>
          <Autocomplete
            multiple
            options={US_STATES}
            value={formData.operationalStates || []}
            onChange={(event, newValue) => {
              const addressState = formData.address?.state;
              const next =
                addressState && !newValue.includes(addressState)
                  ? [...newValue, addressState]
                  : newValue;
              setFormData({ ...formData, operationalStates: next });
              if (errors.operationalStates) {
                setErrors({ ...errors, operationalStates: "" });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select states"
                error={!!errors.operationalStates}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 40,
                    minHeight: 40,
                    maxHeight: 40,
                    alignItems: "center",
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    overflowY: "hidden",
                    "& .MuiAutocomplete-tag": {
                      margin: "0 4px 0 0",
                      maxHeight: 24,
                      flexShrink: 0,
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: 0,
                      minWidth: 80,
                    },
                    "& fieldset": {
                      paddingLeft: 1,
                    },
                  },
                  "& .MuiAutocomplete-endAdornment": {
                    top: "50%",
                    transform: "translateY(-50%)",
                  },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const tagProps = getTagProps({ index });
                const isAddressState = option === formData.address?.state;
                return (
                  <Chip
                    label={option}
                    size="small"
                    {...tagProps}
                    key={tagProps.key}
                    onDelete={isAddressState ? undefined : tagProps.onDelete}
                  />
                );
              })
            }
          />
          {errors.operationalStates && (
            <SoftTypography variant="caption" color="error" mt={0.25} display="block">
              {errors.operationalStates}
            </SoftTypography>
          )}
          <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
            States where the organization operates
          </SoftTypography>
        </Grid>

        {/* Coverage Areas - EXTRA FIELD NOT IN SCHEMA */}
        {/* <Grid item xs={12} md={6}>
          <SoftBox mb={1}>
            <SoftTypography variant="caption" fontWeight="bold">
              Coverage Areas (States)
            </SoftTypography>
          </SoftBox>
          <Autocomplete
            multiple
            options={US_STATES}
            value={formData.coverageAreas}
            onChange={(event, newValue) => handleChange("coverageAreas", newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select states"
                size="small"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  size="small"
                  {...getTagProps({ index })}
                  key={option}
                />
              ))
            }
          />
        </Grid> */}

        {/* Organization Description */}
        <Grid item xs={12}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Organization Description
            </SoftTypography>
          </SoftBox>
          <SoftInput
            multiline
            rows={3}
            placeholder="Brief description of your organization..."
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
          />
        </Grid>

        {/* Logo Upload — image preview only, single image file */}
        <Grid item xs={12} md={6}>
          <SoftBox mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              Organization Logo
            </SoftTypography>
          </SoftBox>
          <input
            type="file"
            accept={LOGO_ACCEPT}
            onChange={handleLogoChange}
            style={{ display: "none" }}
            id="logo-upload"
          />
          <label htmlFor="logo-upload" style={{ cursor: "pointer", display: "block" }}>
            <SoftBox
              sx={{
                minHeight: 120,
                borderRadius: "8px",
                border: "1px dashed #cfd8dc",
                bgcolor: "grey.50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                px: 1.5,
                py: 1.5,
              }}
            >
              {logoPreview ? (
                <SoftBox
                  component="img"
                  src={logoPreview}
                  alt="Organization logo"
                  sx={{
                    maxWidth: "100%",
                    maxHeight: 120,
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              ) : (
                <SoftBox textAlign="center">
                  <Icon sx={{ color: "text.secondary", mb: 0.5 }}>image</Icon>
                  <SoftTypography variant="caption" color="text" display="block">
                    Choose an image
                  </SoftTypography>
                </SoftBox>
              )}
            </SoftBox>
          </label>
          {errors.logo && (
            <SoftTypography
              variant="caption"
              color="error"
              mt={0.25}
              display="block"
            >
              {errors.logo}
            </SoftTypography>
          )}
        </Grid>

        {/* First Admin - EXTRA FIELD NOT IN SCHEMA */}
        {/* <Grid item xs={12} md={6}>
          <SoftBox mb={1}>
            <SoftTypography variant="caption" fontWeight="bold">
              First Admin (User ID) *
            </SoftTypography>
          </SoftBox>
          <Autocomplete
            options={availableUsers}
            getOptionLabel={(option) => `${option.name} (ID: ${option.id})`}
            value={availableUsers.find((u) => u.id === formData.firstAdminId) || null}
            onChange={(event, newValue) =>
              handleChange("firstAdminId", newValue ? newValue.id : "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Search by user ID or name"
                size="small"
                error={!!errors.firstAdminId}
              />
            )}
          />
          {errors.firstAdminId && (
            <SoftTypography variant="caption" color="error">
              {errors.firstAdminId}
            </SoftTypography>
          )}
        </Grid> */}

        {/* Insurance - EXTRA FIELD NOT IN SCHEMA */}
        {/* <Grid item xs={12} md={6}>
          <SoftBox mb={1}>
            <SoftTypography variant="caption" fontWeight="bold">
              Insurance Information
            </SoftTypography>
          </SoftBox>
          <SoftInput
            placeholder="Insurance provider and policy number"
            value={formData.insurance}
            onChange={(e) => handleChange("insurance", e.target.value)}
          />
        </Grid> */}

        {/* Info Box */}
        {/* <Grid item xs={12}>
          <SoftBox
            bgColor="success"
            variant="gradient"
            borderRadius="lg"
            p={2}
            mt={2}
          >
            <SoftTypography variant="caption" color="white" fontWeight="bold">
              What happens next?
            </SoftTypography>
            <SoftTypography variant="caption" color="white" display="block" mt={1}>
              • Organization will be created with provided details
            </SoftTypography>
            <SoftTypography variant="caption" color="white" display="block">
              • Selected user will become the first admin
            </SoftTypography>
            <SoftTypography variant="caption" color="white" display="block">
              • Welcome email will be sent to organization email
            </SoftTypography>
            <SoftTypography variant="caption" color="white" display="block">
              • Admin will receive notification email
            </SoftTypography>
          </SoftBox>
        </Grid> */}
      </Grid>

      {/* Action Buttons */}
      <SoftBox mt={1.5} display="flex" justifyContent="flex-end" gap={2}>
        <SoftButton variant="outlined" color="secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </SoftButton>
        <SoftButton
          variant="gradient"
          color="success"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
              Saving...
            </>
          ) : (
            <>
              <Icon sx={{ mr: 1 }}>save</Icon>
              {initialData ? "Update Non-Profit" : "Create Non-Profit"}
            </>
          )}
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );
}

export default NonprofitForm;
